import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startOfDay, subDays, subMonths, subYears } from 'date-fns';

import MinimalisticDropDown from '@/shared/components/ui/MinimalisticDropDown';
import {
  fetchOutcomeStepEventsApi,
  type ListStepOutcomeEventsParams,
  type StepOutcomeEvent,
} from '@/features/system/orchestrator/api/outcomeEventsApi';

const DEFAULT_WINDOW_HOURS = 24;
const DEFAULT_LIMIT = 200;

function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

function safeText(v: unknown): string {
  const s = String(v ?? '').trim();
  return s;
}

function sortUnique(values: string[]): string[] {
  const out = Array.from(new Set(values.filter(Boolean)));
  out.sort((a, b) => a.localeCompare(b));
  return out;
}

function fmtJsonOneLine(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return '';
    // Try to compress JSON strings.
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try {
        return JSON.stringify(JSON.parse(s));
      } catch {
        return s;
      }
    }
    return s;
  }
  try {
    return JSON.stringify(v);
  } catch {
    try {
      return String(v);
    } catch {
      return '';
    }
  }
}

function toDatetimeLocalValue(iso: string): string {
  // input[type=datetime-local] expects: YYYY-MM-DDTHH:mm
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fromDatetimeLocalValue(v: string): string | null {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}

export default function WorkflowOutcomesPanel() {
  const now = Date.now();
  const defaultFromIso = toIso(now - DEFAULT_WINDOW_HOURS * 60 * 60 * 1000);
  const defaultToIso = toIso(now);

  const [fromLocal, setFromLocal] = useState<string>(toDatetimeLocalValue(defaultFromIso));
  const [toLocal, setToLocal] = useState<string>(toDatetimeLocalValue(defaultToIso));

  const [rangePreset, setRangePreset] = useState<'today' | 'last_week' | 'last_month' | 'last_6_months' | 'last_year' | 'custom'>('custom');

  const applyPreset = (preset: typeof rangePreset) => {
    setRangePreset(preset);
    if (preset === 'custom') return;

    const now = new Date();
    const to = now;
    const from = (() => {
      switch (preset) {
        case 'today':
          return startOfDay(now);
        case 'last_week':
          return subDays(now, 7);
        case 'last_month':
          return subMonths(now, 1);
        case 'last_6_months':
          return subMonths(now, 6);
        case 'last_year':
          return subYears(now, 1);
        default:
          return startOfDay(now);
      }
    })();

    setFromLocal(toDatetimeLocalValue(from.toISOString()));
    setToLocal(toDatetimeLocalValue(to.toISOString()));
  };

  const [workerKey, setWorkerKey] = useState<string>('');
  const [handlerKeyBase, setHandlerKeyBase] = useState<string>('');
  const [handlerNamespace, setHandlerNamespace] = useState<string>('');

  const params: ListStepOutcomeEventsParams = useMemo(() => {
    const from = fromDatetimeLocalValue(fromLocal);
    const to = fromDatetimeLocalValue(toLocal);

    return {
      include_history: true,
      from: from ?? undefined,
      to: to ?? undefined,
      limit: DEFAULT_LIMIT,
      worker_key: workerKey || undefined,
      handler_key_base: handlerKeyBase || undefined,
      handler_namespace: handlerNamespace || undefined,
    };
  }, [fromLocal, toLocal, workerKey, handlerKeyBase, handlerNamespace]);

  const query = useQuery({
    queryKey: ['developer', 'workflowOutcomes', 'stepOutcomeEvents', params] as const,
    queryFn: async () => {
      // If date inputs are invalid, do not fetch.
      if (!params.from || !params.to) {
        return { items: [] as StepOutcomeEvent[] } as any;
      }
      return await fetchOutcomeStepEventsApi(params);
    },
    staleTime: 5_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const items: StepOutcomeEvent[] = Array.isArray((query.data as any)?.items) ? ((query.data as any).items as StepOutcomeEvent[]) : [];

  const workerOptions = useMemo(() => {
    const values = items.map((e) => safeText((e as any).worker_key)).filter(Boolean);
    return sortUnique(values).map((v) => ({ value: v, label: v }));
  }, [items]);

  const handlerBaseOptions = useMemo(() => {
    const values = items.map((e) => safeText((e as any).handler_key_base)).filter(Boolean);
    return sortUnique(values).map((v) => ({ value: v, label: v }));
  }, [items]);

  const namespaceOptions = useMemo(() => {
    const values = items.map((e) => safeText((e as any).handler_namespace)).filter(Boolean);
    return sortUnique(values).map((v) => ({ value: v, label: v }));
  }, [items]);

  return (
    <div className="h-full min-h-0 rounded-md border border-neutral-800 flex flex-col">
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
        <div className="text-sm text-neutral-200">Outcomes</div>
        <div className="text-xs text-neutral-500">GET /orchestrator/outcomes/steps (workflow_step_outcomes_list_v2)</div>
      </div>

      <div className="p-3 border-b border-neutral-800 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">From</div>
          <input
            className="h-9 px-2 rounded-md pac-glass-input text-neutral-100 text-sm"
            type="datetime-local"
            value={fromLocal}
            onChange={(e) => {
              setRangePreset('custom');
              setFromLocal(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">To</div>
          <input
            className="h-9 px-2 rounded-md pac-glass-input text-neutral-100 text-sm"
            type="datetime-local"
            value={toLocal}
            onChange={(e) => {
              setRangePreset('custom');
              setToLocal(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">Worker</div>
          <MinimalisticDropDown
            value={workerKey}
            onChange={(v) => setWorkerKey(String(v ?? ''))}
            options={workerOptions as any}
            placeholder="All"
            minWidth={240}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">Handler base</div>
          <MinimalisticDropDown
            value={handlerKeyBase}
            onChange={(v) => setHandlerKeyBase(String(v ?? ''))}
            options={handlerBaseOptions as any}
            placeholder="All"
            minWidth={240}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">Namespace</div>
          <MinimalisticDropDown
            value={handlerNamespace}
            onChange={(v) => setHandlerNamespace(String(v ?? ''))}
            options={namespaceOptions as any}
            placeholder="All"
            minWidth={240}
          />
        </div>

        <div className="flex-1" />

        <button
          className="h-9 px-3 rounded-md bg-transparent border border-neutral-700 text-neutral-100 text-sm hover:border-neutral-500"
          onClick={() => query.refetch()}
        >
          Refresh
        </button>

        <div className="w-full order-last flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-400">
          <button
            type="button"
            onClick={() => applyPreset('today')}
            className={(rangePreset === 'today' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Today
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_week')}
            className={(rangePreset === 'last_week' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last week
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_month')}
            className={(rangePreset === 'last_month' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last month
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_6_months')}
            className={(rangePreset === 'last_6_months' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last 6 months
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_year')}
            className={(rangePreset === 'last_year' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last year
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('custom')}
            className={(rangePreset === 'custom' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Custom
          </button>
        </div>
      </div>

      <div className="p-3 flex-1 min-h-0 overflow-auto space-y-3">
        {!params.from || !params.to ? (
          <div className="text-sm text-amber-300">Set valid From/To to query outcomes.</div>
        ) : query.isLoading ? (
          <div className="text-sm text-neutral-400">Loading outcomes…</div>
        ) : query.error ? (
          <div className="text-sm text-rose-400">Failed to load outcomes: {(query.error as any)?.message ?? String(query.error)}</div>
        ) : (
          <>
            <div className="text-xs text-neutral-500">
              Showing up to {DEFAULT_LIMIT} rows. Window: {params.from} → {params.to}
            </div>

            {items.length === 0 ? (
              <div className="text-sm text-neutral-600">No outcomes found for the selected filters.</div>
            ) : (
              <div className="rounded-md border border-neutral-800 overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-neutral-800">
                    <tr>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Created</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Workflow</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Step</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Handler</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Worker</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Handler base</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Namespace</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Outcome key</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {items.map((e) => {
                      const created = safeText((e as any).created_at);
                      const workflow = safeText((e as any).workflow_key) || '—';
                      const step = safeText((e as any).step_key) || '—';
                      const handler = safeText((e as any).handler_key) || '—';
                      const worker = safeText((e as any).worker_key) || '—';
                      const base = safeText((e as any).handler_key_base) || '—';
                      const ns = safeText((e as any).handler_namespace) || '—';
                      const outcomeKey = safeText((e as any).outcome_key) || '—';
                      const value = fmtJsonOneLine((e as any).value);
                      const key = `${(e as any).outcome_id ?? ''}:${(e as any).run_id ?? ''}:${created}`;

                      return (
                        <tr key={key} className="hover:bg-white/[0.02] align-top">
                          <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap">{created}</td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={workflow}>{workflow}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={step}>{step}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={handler}>{handler}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={worker}>{worker}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={base}>{base}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={ns}>{ns}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={outcomeKey}>{outcomeKey}</div></td>
                          <td className="px-2 py-1 text-neutral-300 font-mono max-w-[36rem]"><div className="truncate" title={value}>{value || '—'}</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
